import { AIModule } from '../types';

export const IOT_EDGE_AI_FUNDAMENTALS_MODULES: AIModule[] = [
  {
    id: 'iot-edge-intro',
    title: 'Introduction to IoT and Edge AI',
    description: 'Understanding IoT ecosystems, edge computing paradigms, and the convergence with AI.',
    duration: '2 hours',
    difficulty: 'beginner',
    content: `
# Introduction to IoT and Edge AI

## What is IoT (Internet of Things)?

The Internet of Things refers to the network of physical devices embedded with sensors, software, and connectivity that enables them to collect and exchange data.

### Key Components:
- **Sensors**: Collect environmental data (temperature, motion, light, etc.)
- **Connectivity**: WiFi, Bluetooth, cellular, LoRaWAN, etc.
- **Data Processing**: Local or cloud-based analytics
- **Actuators**: Execute actions based on decisions
- **User Interface**: Mobile apps, dashboards, voice control

## What is Edge Computing?

Edge computing brings computation and data storage closer to the sources of data, reducing latency and bandwidth usage.

### Benefits of Edge Computing:
- **Reduced Latency**: Process data locally for real-time responses
- **Bandwidth Efficiency**: Reduce data transmission to cloud
- **Privacy & Security**: Keep sensitive data local
- **Reliability**: Continue operating when connectivity is poor
- **Cost Reduction**: Lower cloud processing costs

## Edge AI: The Convergence

Edge AI combines IoT devices with artificial intelligence capabilities at the network edge.

### Key Advantages:
1. **Real-time Decision Making**: Immediate responses without cloud latency
2. **Privacy Preservation**: Sensitive data stays on device
3. **Reduced Bandwidth**: Only insights, not raw data, sent to cloud
4. **Offline Operation**: AI works even without internet connection
5. **Scalability**: Distribute processing across many edge devices

## IoT-Edge AI Architecture

### Three-Tier Architecture:
1. **Device Layer**: Sensors, actuators, edge devices
2. **Edge Layer**: Local processing, storage, AI inference
3. **Cloud Layer**: Model training, management, analytics

### Data Flow:
Sensors → Edge Processing → Local AI → Actions/Cloud

## Common Use Cases

### Smart Home
- **Voice assistants**: Local speech recognition
- **Security cameras**: Real-time object detection
- **Thermostats**: Predictive temperature control
- **Lighting**: Occupancy-based automation

### Industrial IoT (IIoT)
- **Predictive maintenance**: Equipment failure prediction
- **Quality control**: Defect detection in manufacturing
- **Asset tracking**: Real-time location monitoring
- **Process optimization**: Dynamic parameter adjustment

### Smart Cities
- **Traffic management**: Real-time traffic flow optimization
- **Environmental monitoring**: Air quality and noise levels
- **Public safety**: Crowd monitoring and emergency detection
- **Energy management**: Smart grid optimization

### Healthcare
- **Wearable devices**: Continuous health monitoring
- **Medical imaging**: Edge-based diagnostic assistance
- **Patient monitoring**: Real-time vital sign analysis
- **Emergency response**: Automated alert systems

## Challenges in IoT Edge AI

### 1. Resource Constraints
- **Limited Processing Power**: Mobile CPUs, limited RAM
- **Battery Life**: Power-efficient algorithms required
- **Storage**: Minimal local storage capacity
- **Memory**: Constraints on model size and complexity

### 2. Connectivity Issues
- **Intermittent Connectivity**: Must operate offline
- **Bandwidth Limitations**: Efficient data transmission
- **Network Latency**: Variable connection quality
- **Protocol Diversity**: Multiple communication standards

### 3. Security & Privacy
- **Device Security**: Vulnerable endpoints
- **Data Privacy**: Sensitive information on devices
- **Update Management**: Secure over-the-air updates
- **Authentication**: Device and user verification

### 4. Model Deployment
- **Model Optimization**: Compression and quantization
- **Hardware Compatibility**: Different chipsets and architectures
- **Version Management**: Model updates and rollbacks
- **Performance Monitoring**: Tracking model accuracy in production
`,
    objectives: [
      'Understand IoT ecosystem components and architecture',
      'Explain benefits and challenges of edge computing',
      'Identify appropriate use cases for Edge AI',
      'Recognize resource constraints and design considerations'
    ],
    codeExamples: [
      {
        id: 'simple-iot-simulation',
        title: 'Simple IoT Device Simulation',
        description: 'Basic simulation of IoT sensors and edge processing',
        code: `
import time
import random
import json
from datetime import datetime
from typing import Dict, List, Any
import numpy as np

class IoTSensor:
    def __init__(self, sensor_id: str, sensor_type: str, location: str):
        self.sensor_id = sensor_id
        self.sensor_type = sensor_type
        self.location = location
        self.is_active = True
        self.last_reading = None
    
    def read(self) -> Dict[str, Any]:
        """Simulate sensor reading based on type"""
        timestamp = datetime.now().isoformat()
        
        if self.sensor_type == "temperature":
            # Simulate temperature sensor (Celsius)
            value = round(random.uniform(18.0, 30.0), 2)
            unit = "°C"
        elif self.sensor_type == "humidity":
            # Simulate humidity sensor (percentage)
            value = round(random.uniform(30.0, 80.0), 2)
            unit = "%"
        elif self.sensor_type == "motion":
            # Simulate motion detector (boolean)
            value = random.choice([True, False])
            unit = "detected"
        elif self.sensor_type == "light":
            # Simulate light sensor (lux)
            value = round(random.uniform(0, 1000), 2)
            unit = "lux"
        elif self.sensor_type == "air_quality":
            # Simulate air quality sensor (AQI)
            value = random.randint(0, 300)
            unit = "AQI"
        else:
            value = random.uniform(0, 100)
            unit = "units"
        
        reading = {
            "sensor_id": self.sensor_id,
            "sensor_type": self.sensor_type,
            "location": self.location,
            "timestamp": timestamp,
            "value": value,
            "unit": unit,
            "status": "active" if self.is_active else "inactive"
        }
        
        self.last_reading = reading
        return reading

class EdgeProcessor:
    def __init__(self, processor_id: str):
        self.processor_id = processor_id
        self.sensors = {}
        self.data_buffer = []
        self.rules = []
        self.alerts = []
    
    def add_sensor(self, sensor: IoTSensor):
        """Add sensor to edge processor"""
        self.sensors[sensor.sensor_id] = sensor
        print(f"Added sensor {sensor.sensor_id} ({sensor.sensor_type}) to edge processor")
    
    def add_rule(self, rule_func, rule_name: str):
        """Add processing rule to edge processor"""
        self.rules.append({"name": rule_name, "function": rule_func})
        print(f"Added rule: {rule_name}")
    
    def collect_data(self) -> List[Dict]:
        """Collect data from all sensors"""
        readings = []
        for sensor in self.sensors.values():
            if sensor.is_active:
                reading = sensor.read()
                readings.append(reading)
                self.data_buffer.append(reading)
        
        # Keep only recent data (last 100 readings)
        if len(self.data_buffer) > 100:
            self.data_buffer = self.data_buffer[-100:]
        
        return readings
    
    def process_data(self, readings: List[Dict]):
        """Process data using defined rules"""
        for reading in readings:
            for rule in self.rules:
                try:
                    result = rule["function"](reading, self.data_buffer)
                    if result:
                        alert = {
                            "timestamp": datetime.now().isoformat(),
                            "rule": rule["name"],
                            "sensor_id": reading["sensor_id"],
                            "message": result,
                            "reading": reading
                        }
                        self.alerts.append(alert)
                        print(f"ALERT: {alert['message']}")
                except Exception as e:
                    print(f"Error processing rule {rule['name']}: {e}")
    
    def get_recent_alerts(self, limit: int = 10) -> List[Dict]:
        """Get recent alerts"""
        return self.alerts[-limit:]
    
    def get_sensor_summary(self) -> Dict:
        """Get summary of sensor data"""
        summary = {}
        for sensor_type in set(s.sensor_type for s in self.sensors.values()):
            type_data = [r for r in self.data_buffer if r["sensor_type"] == sensor_type]
            if type_data:
                values = [r["value"] for r in type_data if isinstance(r["value"], (int, float))]
                if values:
                    summary[sensor_type] = {
                        "count": len(type_data),
                        "avg": round(np.mean(values), 2),
                        "min": min(values),
                        "max": max(values),
                        "latest": type_data[-1]["value"]
                    }
        return summary

# Example rules for edge processing
def temperature_alert_rule(reading: Dict, data_buffer: List[Dict]) -> str:
    """Alert if temperature is too high or too low"""
    if reading["sensor_type"] == "temperature":
        temp = reading["value"]
        if temp > 28.0:
            return f"High temperature detected: {temp}°C"
        elif temp < 20.0:
            return f"Low temperature detected: {temp}°C"
    return None

def motion_pattern_rule(reading: Dict, data_buffer: List[Dict]) -> str:
    """Alert if motion detected after period of no motion"""
    if reading["sensor_type"] == "motion" and reading["value"]:
        # Check if there was no motion in last 5 readings
        recent_motion = [r for r in data_buffer[-5:] 
                        if r["sensor_type"] == "motion" and r["sensor_id"] == reading["sensor_id"]]
        
        if len(recent_motion) >= 3:
            previous_values = [r["value"] for r in recent_motion[:-1]]
            if not any(previous_values):  # No motion in previous readings
                return f"Motion detected after period of inactivity"
    return None

def air_quality_trend_rule(reading: Dict, data_buffer: List[Dict]) -> str:
    """Alert if air quality is degrading"""
    if reading["sensor_type"] == "air_quality":
        current_aqi = reading["value"]
        if current_aqi > 150:
            return f"Poor air quality detected: AQI {current_aqi}"
        
        # Check trend over last 10 readings
        recent_aqi = [r["value"] for r in data_buffer[-10:] 
                     if r["sensor_type"] == "air_quality" and r["sensor_id"] == reading["sensor_id"]]
        
        if len(recent_aqi) >= 5:
            avg_recent = np.mean(recent_aqi[-3:])
            avg_older = np.mean(recent_aqi[:3])
            if avg_recent > avg_older * 1.2:  # 20% increase
                return f"Air quality degrading trend detected"
    return None

# Simulation example
def run_iot_simulation():
    """Run IoT edge simulation"""
    print("Starting IoT Edge AI Simulation...")
    
    # Create edge processor
    edge = EdgeProcessor("edge_001")
    
    # Create sensors
    sensors = [
        IoTSensor("temp_01", "temperature", "living_room"),
        IoTSensor("humid_01", "humidity", "living_room"),
        IoTSensor("motion_01", "motion", "front_door"),
        IoTSensor("light_01", "light", "bedroom"),
        IoTSensor("air_01", "air_quality", "kitchen")
    ]
    
    # Add sensors to edge processor
    for sensor in sensors:
        edge.add_sensor(sensor)
    
    # Add processing rules
    edge.add_rule(temperature_alert_rule, "Temperature Monitor")
    edge.add_rule(motion_pattern_rule, "Motion Pattern Detector")
    edge.add_rule(air_quality_trend_rule, "Air Quality Trend Monitor")
    
    print("\\nStarting data collection and processing...")
    
    # Simulate real-time processing
    for cycle in range(20):
        print(f"\\n--- Cycle {cycle + 1} ---")
        
        # Collect data from sensors
        readings = edge.collect_data()
        
        # Process data with rules
        edge.process_data(readings)
        
        # Display current readings
        for reading in readings:
            print(f"{reading['sensor_type'].title()} ({reading['sensor_id']}): "
                  f"{reading['value']} {reading['unit']}")
        
        # Simulate some sensor issues occasionally
        if random.random() < 0.1:  # 10% chance
            faulty_sensor = random.choice(sensors)
            faulty_sensor.is_active = not faulty_sensor.is_active
            status = "activated" if faulty_sensor.is_active else "deactivated"
            print(f"Sensor {faulty_sensor.sensor_id} {status}")
        
        time.sleep(1)  # Wait 1 second between cycles
    
    # Print summary
    print("\\n" + "="*50)
    print("SIMULATION SUMMARY")
    print("="*50)
    
    summary = edge.get_sensor_summary()
    for sensor_type, stats in summary.items():
        print(f"\\n{sensor_type.title()}:")
        print(f"  Readings: {stats['count']}")
        print(f"  Average: {stats['avg']}")
        print(f"  Range: {stats['min']} - {stats['max']}")
        print(f"  Latest: {stats['latest']}")
    
    recent_alerts = edge.get_recent_alerts()
    print(f"\\nTotal Alerts Generated: {len(edge.alerts)}")
    if recent_alerts:
        print("\\nRecent Alerts:")
        for alert in recent_alerts[-5:]:  # Show last 5 alerts
            print(f"  {alert['timestamp'][:19]}: {alert['message']}")

# Run the simulation
if __name__ == "__main__":
    run_iot_simulation()
`,
        language: 'python',
        category: 'demo'
      }
    ],
    quiz: {
      questions: [
        {
          id: 'edge-ai-benefit',
          question: 'What is the primary advantage of processing AI inference at the edge rather than in the cloud?',
          options: [
            'Lower cost of hardware',
            'Reduced latency and real-time response',
            'Higher accuracy of models',
            'Easier model deployment'
          ],
          correctAnswer: 1,
          explanation: 'Edge AI processing provides reduced latency and enables real-time responses by avoiding round-trip communication to the cloud.'
        }
      ]
    }
  },
  {
    id: 'edge-deployment',
    title: 'Edge AI Deployment and Optimization',
    description: 'Model compression, quantization, hardware acceleration, and deployment strategies.',
    duration: '3.5 hours',
    difficulty: 'intermediate',
    content: `
# Edge AI Deployment and Optimization

## Model Optimization for Edge Devices

### 1. Model Compression Techniques

#### Pruning
Remove unnecessary connections or neurons from neural networks:
- **Magnitude-based pruning**: Remove weights below a threshold
- **Structured pruning**: Remove entire neurons or channels
- **Gradual pruning**: Incrementally remove weights during training

#### Knowledge Distillation
Transfer knowledge from large "teacher" model to smaller "student" model:
- **Response-based distillation**: Match final outputs
- **Feature-based distillation**: Match intermediate representations
- **Attention-based distillation**: Transfer attention mechanisms

#### Low-Rank Factorization
Decompose weight matrices into smaller matrices:
- **Singular Value Decomposition (SVD)**
- **Tucker decomposition for tensors**
- **CP decomposition for efficient computation**

### 2. Quantization

#### Post-Training Quantization
Convert trained models to lower precision:
- **INT8 quantization**: 8-bit integers instead of 32-bit floats
- **Dynamic quantization**: Quantize only weights, keep activations in float
- **Static quantization**: Quantize both weights and activations

#### Quantization-Aware Training
Train models with quantization in mind:
- **Fake quantization**: Simulate quantization during training
- **Learned quantization scales**: Optimize quantization parameters
- **Mixed precision**: Use different precisions for different layers

### 3. Neural Architecture Search (NAS)
Automatically find efficient architectures:
- **Mobile-optimized architectures**: MobileNet, EfficientNet
- **Hardware-aware NAS**: Consider specific device constraints
- **Progressive shrinking**: Start large and gradually reduce model size

## Hardware Acceleration

### 1. Specialized Processors

#### AI Accelerators
- **Google Edge TPU**: Tensor Processing Unit for edge inference
- **Intel Movidius**: Neural compute sticks
- **NVIDIA Jetson**: GPU-based edge computing platforms
- **Qualcomm AI Engine**: Mobile AI processing

#### FPGA (Field-Programmable Gate Arrays)
- **Custom logic**: Programmable hardware for specific tasks
- **Low latency**: Dedicated circuits for inference
- **Power efficiency**: Optimized for specific operations

### 2. Software Frameworks

#### TensorFlow Lite
Optimized for mobile and edge devices:
- **Model conversion**: Convert TF models to TFLite format
- **Quantization support**: Built-in quantization tools
- **Hardware delegation**: GPU, DSP, NPU acceleration

#### ONNX Runtime
Cross-platform inference optimization:
- **Model format standardization**: Platform-independent models
- **Execution providers**: CPU, GPU, edge accelerator support
- **Graph optimization**: Automatic performance improvements

#### Apache TVM
Deep learning compiler stack:
- **Auto-tuning**: Optimize for specific hardware
- **Code generation**: Generate efficient kernels
- **Cross-compilation**: Target various architectures

## Deployment Strategies

### 1. Model Formats and Conversion

#### TensorFlow Lite Conversion
\`\`\`python
# Convert TensorFlow model to TFLite
converter = tf.lite.TFLiteConverter.from_saved_model('model_path')
converter.optimizations = [tf.lite.Optimize.DEFAULT]
tflite_model = converter.convert()
\`\`\`

#### ONNX Conversion
\`\`\`python
# Convert PyTorch to ONNX
torch.onnx.export(model, dummy_input, "model.onnx")
\`\`\`

### 2. Runtime Optimization

#### Memory Management
- **Model loading**: Load models on-demand
- **Memory pooling**: Reuse memory buffers
- **Garbage collection**: Manage memory allocation

#### Batch Processing
- **Dynamic batching**: Combine multiple requests
- **Optimal batch sizes**: Balance latency and throughput
- **Pipeline parallelism**: Overlap computation and I/O

### 3. Model Serving

#### Edge Inference Servers
- **TensorFlow Serving**: Model serving system
- **TorchServe**: PyTorch model serving
- **NVIDIA Triton**: Multi-framework serving

#### Containerization
- **Docker containers**: Portable deployment packages
- **Kubernetes**: Container orchestration
- **Edge-specific orchestrators**: K3s, MicroK8s

## Performance Monitoring

### 1. Metrics to Track
- **Inference latency**: Time per prediction
- **Memory usage**: RAM and storage consumption
- **CPU/GPU utilization**: Hardware resource usage
- **Power consumption**: Battery life impact
- **Model accuracy**: Performance degradation monitoring

### 2. Profiling Tools
- **TensorFlow Profiler**: Performance analysis
- **PyTorch Profiler**: Memory and computation profiling
- **Hardware profilers**: Platform-specific tools

### 3. A/B Testing
- **Model comparison**: Test different optimizations
- **Gradual rollout**: Deploy to subset of devices
- **Performance monitoring**: Track real-world performance

## Security Considerations

### 1. Model Protection
- **Model encryption**: Protect intellectual property
- **Secure enclaves**: Hardware-based protection
- **Obfuscation**: Make reverse engineering difficult

### 2. Input Validation
- **Adversarial detection**: Identify malicious inputs
- **Input sanitization**: Clean and validate data
- **Rate limiting**: Prevent abuse

### 3. Over-the-Air Updates
- **Secure channels**: Encrypted model updates
- **Verification**: Ensure model integrity
- **Rollback capability**: Revert problematic updates
`,
    objectives: [
      'Apply model compression techniques for edge deployment',
      'Implement quantization for mobile devices',
      'Choose appropriate hardware acceleration options',
      'Deploy and monitor edge AI applications'
    ],
    codeExamples: [
      {
        id: 'model-quantization',
        title: 'Model Quantization for Edge Deployment',
        description: 'Implementation of quantization techniques for mobile deployment',
        code: `
import torch
import torch.nn as nn
import torch.quantization as quant
import torchvision.models as models
import numpy as np
import time
from torch.utils.data import DataLoader, TensorDataset

class SimpleConvNet(nn.Module):
    def __init__(self, num_classes=10):
        super(SimpleConvNet, self).__init__()
        self.features = nn.Sequential(
            nn.Conv2d(3, 32, 3, padding=1),
            nn.ReLU(inplace=True),
            nn.MaxPool2d(2),
            nn.Conv2d(32, 64, 3, padding=1),
            nn.ReLU(inplace=True),
            nn.MaxPool2d(2),
            nn.Conv2d(64, 128, 3, padding=1),
            nn.ReLU(inplace=True),
            nn.AdaptiveAvgPool2d((4, 4))
        )
        self.classifier = nn.Sequential(
            nn.Dropout(0.5),
            nn.Linear(128 * 4 * 4, 256),
            nn.ReLU(inplace=True),
            nn.Dropout(0.5),
            nn.Linear(256, num_classes)
        )
    
    def forward(self, x):
        x = self.features(x)
        x = x.view(x.size(0), -1)
        x = self.classifier(x)
        return x

class ModelQuantizer:
    def __init__(self, model):
        self.model = model
        self.quantized_model = None
        
    def prepare_model_for_qat(self):
        """Prepare model for Quantization Aware Training"""
        # Fuse conv-bn-relu blocks for better quantization
        self.model = torch.quantization.fuse_modules(
            self.model,
            [['features.0', 'features.1'],  # conv-relu
             ['features.3', 'features.4'],  # conv-relu  
             ['features.6', 'features.7']]  # conv-relu
        )
        
        # Set quantization config
        self.model.qconfig = quant.get_default_qat_qconfig('fbgemm')
        
        # Prepare model for QAT
        quant.prepare_qat(self.model, inplace=True)
        
        print("Model prepared for Quantization Aware Training")
        return self.model
    
    def post_training_quantization(self, calibration_loader):
        """Apply post-training quantization"""
        # Set model to evaluation mode
        self.model.eval()
        
        # Set quantization config for post-training quantization
        self.model.qconfig = quant.get_default_qconfig('fbgemm')
        
        # Prepare model
        quantized_model = quant.prepare(self.model, inplace=False)
        
        # Calibrate with representative data
        print("Calibrating quantized model...")
        with torch.no_grad():
            for batch_idx, (data, _) in enumerate(calibration_loader):
                if batch_idx >= 100:  # Use limited data for calibration
                    break
                quantized_model(data)
        
        # Convert to quantized model
        self.quantized_model = quant.convert(quantized_model, inplace=False)
        
        print("Post-training quantization completed")
        return self.quantized_model
    
    def dynamic_quantization(self):
        """Apply dynamic quantization (weights only)"""
        self.quantized_model = torch.quantization.quantize_dynamic(
            self.model,
            {nn.Linear},  # Quantize linear layers
            dtype=torch.qint8
        )
        
        print("Dynamic quantization completed")
        return self.quantized_model

class ModelCompressor:
    def __init__(self, model):
        self.model = model
        self.original_size = self.get_model_size(model)
    
    def get_model_size(self, model):
        """Calculate model size in MB"""
        param_size = 0
        buffer_size = 0
        
        for param in model.parameters():
            param_size += param.nelement() * param.element_size()
        
        for buffer in model.buffers():
            buffer_size += buffer.nelement() * buffer.element_size()
        
        size_mb = (param_size + buffer_size) / 1024 / 1024
        return size_mb
    
    def magnitude_pruning(self, sparsity=0.5):
        """Apply magnitude-based pruning"""
        for name, module in self.model.named_modules():
            if isinstance(module, nn.Conv2d) or isinstance(module, nn.Linear):
                # Calculate threshold for pruning
                weights = module.weight.data.abs()
                threshold = torch.quantile(weights, sparsity)
                
                # Create mask
                mask = weights > threshold
                
                # Apply pruning
                module.weight.data *= mask.float()
                
                # Count pruned parameters
                pruned = (mask == 0).sum().item()
                total = mask.numel()
                print(f"Pruned {name}: {pruned}/{total} ({100*pruned/total:.1f}%)")
        
        pruned_size = self.get_model_size(self.model)
        compression_ratio = self.original_size / pruned_size
        print(f"Model size: {self.original_size:.2f}MB → {pruned_size:.2f}MB "
              f"(compression: {compression_ratio:.2f}x)")
    
    def structured_pruning(self, pruning_ratio=0.3):
        """Apply structured pruning (remove entire channels)"""
        for name, module in self.model.named_modules():
            if isinstance(module, nn.Conv2d) and module.out_channels > 8:
                # Calculate channel importance (L1 norm)
                channel_importance = torch.norm(module.weight.data, p=1, dim=(1, 2, 3))
                
                # Determine channels to keep
                num_keep = int(module.out_channels * (1 - pruning_ratio))
                _, keep_indices = torch.topk(channel_importance, num_keep)
                keep_indices = sorted(keep_indices.tolist())
                
                # Create new smaller weight tensor
                new_weight = module.weight.data[keep_indices]
                
                # Update module
                module.weight = nn.Parameter(new_weight)
                module.out_channels = len(keep_indices)
                
                if module.bias is not None:
                    module.bias = nn.Parameter(module.bias.data[keep_indices])
                
                print(f"Structured pruning {name}: {module.out_channels} channels kept")

class EdgeBenchmark:
    def __init__(self):
        self.results = {}
    
    def benchmark_model(self, model, model_name, test_loader, device='cpu'):
        """Benchmark model performance"""
        model.to(device)
        model.eval()
        
        # Measure inference time
        times = []
        correct = 0
        total = 0
        
        with torch.no_grad():
            for data, target in test_loader:
                data, target = data.to(device), target.to(device)
                
                # Time inference
                start_time = time.time()
                output = model(data)
                end_time = time.time()
                
                times.append(end_time - start_time)
                
                # Calculate accuracy
                pred = output.argmax(dim=1)
                correct += pred.eq(target).sum().item()
                total += target.size(0)
        
        # Calculate metrics
        avg_time = np.mean(times) * 1000  # Convert to milliseconds
        accuracy = 100. * correct / total
        model_size = self.get_model_size(model)
        
        self.results[model_name] = {
            'accuracy': accuracy,
            'avg_inference_time_ms': avg_time,
            'model_size_mb': model_size,
            'throughput_fps': 1000 / avg_time
        }
        
        print(f"\\n{model_name} Results:")
        print(f"  Accuracy: {accuracy:.2f}%")
        print(f"  Avg Inference Time: {avg_time:.2f}ms")
        print(f"  Model Size: {model_size:.2f}MB")
        print(f"  Throughput: {1000/avg_time:.1f} FPS")
        
        return self.results[model_name]
    
    def get_model_size(self, model):
        """Calculate model size in MB"""
        param_size = 0
        for param in model.parameters():
            param_size += param.nelement() * param.element_size()
        return param_size / 1024 / 1024
    
    def compare_models(self):
        """Compare all benchmarked models"""
        if not self.results:
            print("No models to compare")
            return
        
        print("\\n" + "="*80)
        print("MODEL COMPARISON")
        print("="*80)
        print(f"{'Model':<20} {'Accuracy':<10} {'Time(ms)':<10} {'Size(MB)':<10} {'FPS':<10}")
        print("-" * 80)
        
        for name, results in self.results.items():
            print(f"{name:<20} {results['accuracy']:<10.2f} "
                  f"{results['avg_inference_time_ms']:<10.2f} "
                  f"{results['model_size_mb']:<10.2f} "
                  f"{results['throughput_fps']:<10.1f}")

# Example usage
def run_quantization_example():
    """Demonstrate model quantization techniques"""
    
    # Create sample model
    model = SimpleConvNet(num_classes=10)
    
    # Create sample data
    batch_size = 32
    sample_data = torch.randn(100, 3, 32, 32)
    sample_labels = torch.randint(0, 10, (100,))
    
    dataset = TensorDataset(sample_data, sample_labels)
    dataloader = DataLoader(dataset, batch_size=batch_size, shuffle=True)
    
    # Create benchmark
    benchmark = EdgeBenchmark()
    
    # Benchmark original model
    benchmark.benchmark_model(model, "Original", dataloader)
    
    # Apply dynamic quantization
    quantizer = ModelQuantizer(model)
    dynamic_quantized = quantizer.dynamic_quantization()
    benchmark.benchmark_model(dynamic_quantized, "Dynamic Quantized", dataloader)
    
    # Apply post-training quantization
    model_copy = SimpleConvNet(num_classes=10)
    model_copy.load_state_dict(model.state_dict())
    quantizer_ptq = ModelQuantizer(model_copy)
    ptq_model = quantizer_ptq.post_training_quantization(dataloader)
    benchmark.benchmark_model(ptq_model, "Post-Training Quantized", dataloader)
    
    # Apply pruning
    model_copy2 = SimpleConvNet(num_classes=10)
    model_copy2.load_state_dict(model.state_dict())
    compressor = ModelCompressor(model_copy2)
    compressor.magnitude_pruning(sparsity=0.7)
    benchmark.benchmark_model(model_copy2, "Magnitude Pruned", dataloader)
    
    # Compare all models
    benchmark.compare_models()

if __name__ == "__main__":
    run_quantization_example()
`,
        language: 'python',
        category: 'tutorial'
      }
    ],
    practice: {
      task: 'Implement a complete edge deployment pipeline that includes model optimization, conversion to TensorFlow Lite, and mobile deployment simulation.',
      hints: [
        'Use TensorFlow Lite converter for model optimization',
        'Implement representative dataset for quantization calibration',
        'Simulate mobile inference environment with memory constraints'
      ]
    }
  },
  {
    id: 'smart-iot-systems',
    title: 'Intelligent IoT Systems Design',
    description: 'Building end-to-end intelligent IoT systems with distributed AI and real-time decision making.',
    duration: '4 hours',
    difficulty: 'advanced',
    content: `
# Intelligent IoT Systems Design

## System Architecture Patterns

### 1. Hierarchical Edge Computing
Multi-tier architecture for distributed intelligence:

#### Tier 1: Device Edge
- **Sensors and actuators**: Data collection and action execution
- **Microcontrollers**: Basic processing and filtering
- **Local decision making**: Immediate responses to critical events

#### Tier 2: Local Edge
- **Edge gateways**: Aggregate data from multiple devices
- **Local AI processing**: Complex analytics and ML inference
- **Protocol translation**: Connect different device types

#### Tier 3: Regional Edge
- **Edge data centers**: Regional processing hubs
- **Model training**: Federated learning coordination
- **Cross-system optimization**: Multi-site coordination

#### Tier 4: Cloud
- **Global orchestration**: System-wide management
- **Model development**: Advanced AI training
- **Long-term analytics**: Historical data analysis

### 2. Distributed AI Patterns

#### Federated Learning
Train models across distributed devices without centralizing data:
- **Privacy preservation**: Data stays on local devices
- **Bandwidth efficiency**: Only model updates are shared
- **Personalization**: Models adapt to local conditions
- **Robustness**: System continues if some devices fail

#### Edge-Cloud Collaboration
Hybrid processing between edge and cloud:
- **Dynamic offloading**: Decide what to process where
- **Model cascading**: Simple models at edge, complex in cloud
- **Confidence-based routing**: Route uncertain cases to cloud
- **Adaptive allocation**: Adjust based on resource availability

## Real-Time Decision Making

### 1. Event-Driven Architecture
Respond to events as they occur:
- **Event streams**: Continuous data flows
- **Complex event processing**: Pattern detection across streams
- **Rule engines**: Business logic execution
- **Action triggers**: Automated responses

### 2. Time-Critical Processing
Handle time-sensitive applications:
- **Hard real-time**: Guaranteed response times
- **Soft real-time**: Best-effort low latency
- **Deadline scheduling**: Prioritize time-critical tasks
- **Quality of Service**: Balance accuracy vs latency

### 3. Adaptive Algorithms
Algorithms that adapt to changing conditions:
- **Online learning**: Update models with new data
- **Concept drift detection**: Identify when patterns change
- **Model switching**: Use different models for different conditions
- **Self-healing systems**: Recover from failures automatically

## Communication and Coordination

### 1. IoT Communication Protocols

#### Low-Power Wide-Area (LPWA)
- **LoRaWAN**: Long-range, low-power communication
- **NB-IoT**: Cellular-based narrow-band IoT
- **Sigfox**: Ultra-narrow-band technology

#### Short-Range Communication
- **WiFi**: High bandwidth, moderate power
- **Bluetooth Low Energy**: Personal area networks
- **Zigbee**: Mesh networking for home automation
- **6LoWPAN**: IPv6 over low-power networks

#### Industrial Protocols
- **OPC-UA**: Industrial automation standard
- **MQTT**: Lightweight messaging protocol
- **CoAP**: Constrained application protocol
- **DDS**: Data distribution service for real-time systems

### 2. Data Synchronization
Keep distributed systems consistent:
- **Event sourcing**: Store events rather than state
- **CQRS**: Separate read and write models
- **Eventually consistent**: Accept temporary inconsistencies
- **Conflict resolution**: Handle concurrent updates

## Security and Privacy

### 1. IoT Security Challenges
- **Device constraints**: Limited security capabilities
- **Scale**: Millions of connected devices
- **Heterogeneity**: Different hardware and software
- **Physical access**: Devices in uncontrolled environments

### 2. Security Mechanisms

#### Device Security
- **Secure boot**: Verify device integrity at startup
- **Hardware security modules**: Dedicated security chips
- **Device identity**: Unique, unforgeable device credentials
- **Secure updates**: Authenticated firmware updates

#### Communication Security
- **TLS/DTLS**: Encrypted communication channels
- **Message authentication**: Verify message integrity
- **Key management**: Secure key distribution and rotation
- **Network segmentation**: Isolate IoT networks

#### Data Security
- **Encryption at rest**: Protect stored data
- **Differential privacy**: Add noise to preserve privacy
- **Homomorphic encryption**: Compute on encrypted data
- **Secure multiparty computation**: Joint computation without data sharing

## System Integration

### 1. Interoperability
Connect diverse systems and devices:
- **Standard protocols**: Use common communication standards
- **API gateways**: Translate between different interfaces
- **Semantic mapping**: Convert between data formats
- **Digital twins**: Virtual representations of physical systems

### 2. Scalability Considerations
Handle growing numbers of devices and data:
- **Horizontal scaling**: Add more processing nodes
- **Load balancing**: Distribute work across resources
- **Data partitioning**: Split data across multiple stores
- **Caching strategies**: Reduce data access latency

### 3. Monitoring and Management
Maintain system health and performance:
- **Device management**: Track and control IoT devices
- **Performance monitoring**: Monitor system metrics
- **Predictive maintenance**: Predict and prevent failures
- **Resource optimization**: Optimize resource allocation

## Industry Applications

### 1. Smart Manufacturing (Industry 4.0)
- **Predictive maintenance**: Prevent equipment failures
- **Quality control**: Real-time defect detection
- **Supply chain optimization**: Track materials and products
- **Energy management**: Optimize power consumption

### 2. Smart Cities
- **Traffic optimization**: Dynamic traffic signal control
- **Environmental monitoring**: Air quality and noise tracking
- **Public safety**: Crowd monitoring and emergency response
- **Infrastructure management**: Monitor bridges, water systems

### 3. Smart Agriculture
- **Precision farming**: Optimize irrigation and fertilization
- **Crop monitoring**: Disease and pest detection
- **Livestock tracking**: Animal health and location monitoring
- **Weather stations**: Localized weather data collection

### 4. Smart Healthcare
- **Patient monitoring**: Continuous vital sign tracking
- **Medication management**: Automated drug dispensing
- **Emergency response**: Fall detection and alert systems
- **Telemedicine**: Remote patient consultation support
`,
    objectives: [
      'Design multi-tier IoT architectures with distributed AI',
      'Implement real-time decision making systems',
      'Integrate security and privacy mechanisms',
      'Build scalable and interoperable IoT solutions'
    ],
    codeExamples: [
      {
        id: 'smart-iot-system',
        title: 'Intelligent IoT System Implementation',
        description: 'Complete intelligent IoT system with distributed AI and real-time processing',
        code: `
import asyncio
import json
import time
import random
import threading
from datetime import datetime, timedelta
from typing import Dict, List, Any, Optional, Callable
from dataclasses import dataclass, asdict
from collections import deque
import numpy as np
from abc import ABC, abstractmethod

@dataclass
class IoTMessage:
    device_id: str
    timestamp: str
    message_type: str
    data: Dict[str, Any]
    priority: int = 1  # 1=low, 2=medium, 3=high, 4=critical

@dataclass
class DeviceState:
    device_id: str
    location: str
    device_type: str
    is_online: bool
    last_seen: str
    battery_level: Optional[float] = None
    firmware_version: str = "1.0.0"

class CommunicationProtocol(ABC):
    @abstractmethod
    async def send_message(self, message: IoTMessage) -> bool:
        pass
    
    @abstractmethod
    async def receive_message(self) -> Optional[IoTMessage]:
        pass

class MQTTProtocol(CommunicationProtocol):
    def __init__(self, broker_host: str = "localhost"):
        self.broker_host = broker_host
        self.message_queue = asyncio.Queue()
        self.subscriptions = set()
    
    async def send_message(self, message: IoTMessage) -> bool:
        """Simulate MQTT message sending"""
        print(f"MQTT: Sending message from {message.device_id} - {message.message_type}")
        # In real implementation, would use paho-mqtt or similar
        return True
    
    async def receive_message(self) -> Optional[IoTMessage]:
        """Simulate MQTT message receiving"""
        try:
            message = await asyncio.wait_for(self.message_queue.get(), timeout=0.1)
            return message
        except asyncio.TimeoutError:
            return None

class AIProcessor:
    def __init__(self, processor_id: str):
        self.processor_id = processor_id
        self.models = {}
        self.processing_history = deque(maxlen=1000)
    
    def load_model(self, model_name: str, model_config: Dict):
        """Load AI model for processing"""
        self.models[model_name] = {
            'config': model_config,
            'loaded_at': datetime.now().isoformat(),
            'inference_count': 0
        }
        print(f"AI Processor {self.processor_id}: Loaded model {model_name}")
    
    async def process_data(self, data: Dict[str, Any], model_name: str = None) -> Dict[str, Any]:
        """Process data using AI models"""
        if not model_name and self.models:
            model_name = list(self.models.keys())[0]
        
        if model_name not in self.models:
            return {'error': f'Model {model_name} not found'}
        
        # Simulate AI processing time
        processing_time = random.uniform(0.01, 0.1)
        await asyncio.sleep(processing_time)
        
        # Simulate different types of AI processing
        if 'temperature' in data:
            # Temperature anomaly detection
            temp = data['temperature']
            anomaly_score = abs(temp - 22.0) / 10.0  # Normal range around 22°C
            result = {
                'prediction': 'anomaly' if anomaly_score > 0.5 else 'normal',
                'confidence': max(0.1, 1.0 - anomaly_score),
                'anomaly_score': anomaly_score
            }
        elif 'image' in data:
            # Image classification simulation
            classes = ['person', 'car', 'bike', 'background']
            result = {
                'prediction': random.choice(classes),
                'confidence': random.uniform(0.7, 0.99),
                'bounding_boxes': []
            }
        elif 'vibration' in data:
            # Predictive maintenance
            vibration_level = data['vibration']
            health_score = max(0.0, 1.0 - vibration_level / 100.0)
            result = {
                'prediction': 'maintenance_needed' if health_score < 0.3 else 'healthy',
                'health_score': health_score,
                'estimated_remaining_life': health_score * 100  # days
            }
        else:
            # Generic processing
            result = {
                'prediction': 'processed',
                'confidence': 0.8,
                'processing_time': processing_time
            }
        
        # Update model statistics
        self.models[model_name]['inference_count'] += 1
        
        # Store processing history
        self.processing_history.append({
            'timestamp': datetime.now().isoformat(),
            'model': model_name,
            'input_data': data,
            'result': result,
            'processing_time': processing_time
        })
        
        return result

class EdgeGateway:
    def __init__(self, gateway_id: str, location: str):
        self.gateway_id = gateway_id
        self.location = location
        self.connected_devices = {}
        self.ai_processor = AIProcessor(f"ai_{gateway_id}")
        self.communication = MQTTProtocol()
        self.rules_engine = RulesEngine()
        self.is_running = False
        self.message_buffer = deque(maxlen=1000)
        
    async def start(self):
        """Start the edge gateway"""
        self.is_running = True
        print(f"Edge Gateway {self.gateway_id} started at {self.location}")
        
        # Load AI models
        self.ai_processor.load_model('anomaly_detector', {'type': 'isolation_forest'})
        self.ai_processor.load_model('predictive_maintenance', {'type': 'lstm'})
        
        # Start message processing loop
        asyncio.create_task(self.message_processing_loop())
        
    async def stop(self):
        """Stop the edge gateway"""
        self.is_running = False
        print(f"Edge Gateway {self.gateway_id} stopped")
    
    def register_device(self, device_state: DeviceState):
        """Register a new device with the gateway"""
        self.connected_devices[device_state.device_id] = device_state
        print(f"Device {device_state.device_id} registered with gateway {self.gateway_id}")
    
    async def process_message(self, message: IoTMessage):
        """Process incoming IoT message"""
        # Update device state
        if message.device_id in self.connected_devices:
            self.connected_devices[message.device_id].last_seen = message.timestamp
            self.connected_devices[message.device_id].is_online = True
        
        # Buffer message
        self.message_buffer.append(message)
        
        # Apply rules engine
        rule_results = await self.rules_engine.evaluate(message)
        
        # Process with AI if needed
        ai_results = None
        if message.priority >= 3 or 'anomaly' in message.message_type.lower():
            ai_results = await self.ai_processor.process_data(
                message.data, 
                model_name='anomaly_detector'
            )
        
        # Make decisions based on results
        await self.make_decision(message, rule_results, ai_results)
    
    async def make_decision(self, original_message: IoTMessage, rule_results: List, ai_results: Dict = None):
        """Make intelligent decisions based on processed data"""
        actions = []
        
        # Check rule results
        for rule_result in rule_results:
            if rule_result['action'] == 'alert':
                actions.append({
                    'type': 'send_alert',
                    'message': rule_result['message'],
                    'priority': rule_result.get('priority', 2)
                })
            elif rule_result['action'] == 'actuate':
                actions.append({
                    'type': 'device_control',
                    'device_id': rule_result['target_device'],
                    'command': rule_result['command']
                })
        
        # Check AI results
        if ai_results:
            if ai_results.get('prediction') == 'anomaly' and ai_results.get('confidence', 0) > 0.8:
                actions.append({
                    'type': 'investigation_needed',
                    'device_id': original_message.device_id,
                    'anomaly_score': ai_results.get('anomaly_score', 0),
                    'confidence': ai_results.get('confidence', 0)
                })
            elif ai_results.get('prediction') == 'maintenance_needed':
                actions.append({
                    'type': 'schedule_maintenance',
                    'device_id': original_message.device_id,
                    'estimated_remaining_life': ai_results.get('estimated_remaining_life', 0)
                })
        
        # Execute actions
        for action in actions:
            await self.execute_action(action)
    
    async def execute_action(self, action: Dict):
        """Execute a decision action"""
        if action['type'] == 'send_alert':
            alert_message = IoTMessage(
                device_id=self.gateway_id,
                timestamp=datetime.now().isoformat(),
                message_type='alert',
                data={
                    'alert_message': action['message'],
                    'priority': action['priority']
                },
                priority=action['priority']
            )
            await self.communication.send_message(alert_message)
            print(f"ALERT: {action['message']}")
            
        elif action['type'] == 'device_control':
            control_message = IoTMessage(
                device_id=action['device_id'],
                timestamp=datetime.now().isoformat(),
                message_type='control_command',
                data={'command': action['command']},
                priority=3
            )
            await self.communication.send_message(control_message)
            print(f"CONTROL: Sent {action['command']} to {action['device_id']}")
            
        elif action['type'] == 'investigation_needed':
            print(f"INVESTIGATION: Device {action['device_id']} needs investigation "
                  f"(anomaly score: {action['anomaly_score']:.2f})")
            
        elif action['type'] == 'schedule_maintenance':
            print(f"MAINTENANCE: Device {action['device_id']} needs maintenance "
                  f"(estimated {action['estimated_remaining_life']:.0f} days remaining)")
    
    async def message_processing_loop(self):
        """Main message processing loop"""
        while self.is_running:
            message = await self.communication.receive_message()
            if message:
                await self.process_message(message)
            await asyncio.sleep(0.1)
    
    def get_system_status(self) -> Dict:
        """Get current system status"""
        online_devices = sum(1 for device in self.connected_devices.values() if device.is_online)
        
        return {
            'gateway_id': self.gateway_id,
            'location': self.location,
            'connected_devices': len(self.connected_devices),
            'online_devices': online_devices,
            'messages_processed': len(self.message_buffer),
            'ai_models_loaded': len(self.ai_processor.models),
            'total_inferences': sum(model['inference_count'] for model in self.ai_processor.models.values())
        }

class RulesEngine:
    def __init__(self):
        self.rules = []
        self.load_default_rules()
    
    def load_default_rules(self):
        """Load default processing rules"""
        self.rules = [
            {
                'name': 'high_temperature_alert',
                'condition': lambda msg: msg.data.get('temperature', 0) > 30,
                'action': 'alert',
                'message': 'High temperature detected',
                'priority': 3
            },
            {
                'name': 'low_battery_warning',
                'condition': lambda msg: msg.data.get('battery_level', 100) < 20,
                'action': 'alert',
                'message': 'Low battery warning',
                'priority': 2
            },
            {
                'name': 'motion_detected_security',
                'condition': lambda msg: msg.data.get('motion_detected', False) and msg.data.get('security_mode', False),
                'action': 'alert',
                'message': 'Security breach: motion detected',
                'priority': 4
            },
            {
                'name': 'auto_cooling',
                'condition': lambda msg: msg.data.get('temperature', 0) > 28,
                'action': 'actuate',
                'target_device': 'hvac_system',
                'command': 'start_cooling'
            }
        ]
    
    async def evaluate(self, message: IoTMessage) -> List[Dict]:
        """Evaluate message against all rules"""
        results = []
        
        for rule in self.rules:
            try:
                if rule['condition'](message):
                    result = {
                        'rule_name': rule['name'],
                        'action': rule['action'],
                        'message': rule.get('message', ''),
                        'priority': rule.get('priority', 1)
                    }
                    
                    if 'target_device' in rule:
                        result['target_device'] = rule['target_device']
                    if 'command' in rule:
                        result['command'] = rule['command']
                    
                    results.append(result)
            except Exception as e:
                print(f"Rule evaluation error for {rule['name']}: {e}")
        
        return results

class IoTDevice:
    def __init__(self, device_id: str, device_type: str, location: str, gateway: EdgeGateway):
        self.device_id = device_id
        self.device_type = device_type
        self.location = location
        self.gateway = gateway
        self.is_active = True
        self.sensor_data = {}
        
        # Register with gateway
        device_state = DeviceState(
            device_id=device_id,
            location=location,
            device_type=device_type,
            is_online=True,
            last_seen=datetime.now().isoformat(),
            battery_level=random.uniform(50, 100)
        )
        self.gateway.register_device(device_state)
    
    async def send_sensor_data(self):
        """Send sensor data to gateway"""
        if not self.is_active:
            return
        
        # Generate sensor data based on device type
        if self.device_type == 'temperature_sensor':
            data = {
                'temperature': random.uniform(18, 32),
                'humidity': random.uniform(30, 80)
            }
        elif self.device_type == 'motion_sensor':
            data = {
                'motion_detected': random.choice([True, False]),
                'security_mode': random.choice([True, False])
            }
        elif self.device_type == 'vibration_sensor':
            data = {
                'vibration': random.uniform(0, 50),
                'frequency': random.uniform(10, 100)
            }
        elif self.device_type == 'camera':
            data = {
                'image': f"image_data_{int(time.time())}",
                'objects_detected': random.randint(0, 5)
            }
        else:
            data = {'value': random.uniform(0, 100)}
        
        # Add common fields
        data['battery_level'] = random.uniform(20, 100)
        data['signal_strength'] = random.uniform(-80, -30)  # dBm
        
        message = IoTMessage(
            device_id=self.device_id,
            timestamp=datetime.now().isoformat(),
            message_type='sensor_data',
            data=data,
            priority=random.choice([1, 1, 1, 2, 2, 3])  # Mostly low priority
        )
        
        await self.gateway.process_message(message)

async def run_intelligent_iot_system():
    """Run complete intelligent IoT system simulation"""
    print("Starting Intelligent IoT System Simulation...")
    
    # Create edge gateway
    gateway = EdgeGateway("gateway_001", "Building_A_Floor_3")
    await gateway.start()
    
    # Create IoT devices
    devices = [
        IoTDevice("temp_001", "temperature_sensor", "Room_301", gateway),
        IoTDevice("motion_001", "motion_sensor", "Entrance_A", gateway),
        IoTDevice("vibration_001", "vibration_sensor", "Machine_A", gateway),
        IoTDevice("camera_001", "camera", "Corridor_1", gateway),
        IoTDevice("temp_002", "temperature_sensor", "Room_302", gateway)
    ]
    
    print(f"Created {len(devices)} IoT devices")
    
    # Simulation loop
    simulation_duration = 30  # seconds
    start_time = time.time()
    
    while time.time() - start_time < simulation_duration:
        # Each device sends data periodically
        for device in devices:
            if random.random() < 0.3:  # 30% chance each cycle
                await device.send_sensor_data()
        
        # Print system status periodically
        if int(time.time() - start_time) % 10 == 0:
            status = gateway.get_system_status()
            print(f"\\nSystem Status: {status}")
        
        await asyncio.sleep(1)
    
    # Print final statistics
    print("\\n" + "="*60)
    print("SIMULATION SUMMARY")
    print("="*60)
    
    final_status = gateway.get_system_status()
    for key, value in final_status.items():
        print(f"{key}: {value}")
    
    print(f"\\nAI Processing History (last 10):")
    for record in list(gateway.ai_processor.processing_history)[-10:]:
        print(f"  {record['timestamp']}: {record['model']} -> {record['result']['prediction']}")
    
    await gateway.stop()

# Run the simulation
if __name__ == "__main__":
    asyncio.run(run_intelligent_iot_system())
`,
        language: 'python',
        category: 'tutorial'
      }
    ],
    practice: {
      task: 'Extend the intelligent IoT system to include federated learning capabilities where edge gateways collaboratively train a shared model.',
      hints: [
        'Implement model parameter aggregation across gateways',
        'Add privacy-preserving techniques like differential privacy',
        'Consider network communication patterns for model updates'
      ]
    }
  }
];